/**
 * Settings → Client portal → Rental agreement (superadmin), P7/P7b + #216: per Hollywood Trucks
 * company, which library document clients e-sign, who countersigns, and the signatory's prebuilt
 * signature image the countersign stamps. Changes need a superadmin membership; every one is
 * audited on the server.
 */
import { adminFetch, API_BASE_URL, getAuthHeaders } from '@/lib/adminApi';
import type { EntityKey } from '@/lib/portalCoiSettingsApi';

export interface AgreementChoice { id: string; title: string; file_name: string }
export interface SignatorySetting { name: string; title: string; confirmed_by: string; confirmed_at: string }
export interface SignatureSetting {
  sha256: string; bytes: number; width: number; height: number; uploaded_by: string; uploaded_at: string;
  /** Whose signature it is: it is used only while this person is the signatory. */
  signatory_name: string;
}

/** The same person, whatever the spacing or case (mirrors the server's sameSignatory). */
export function sameSignatory(a: string | null | undefined, b: string | null | undefined): boolean {
  const norm = (n: string | null | undefined) => (n ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return Boolean(norm(a)) && norm(a) === norm(b);
}
export interface EntityAgreementSettings {
  agreement: { document_id: string; set_by: string | null; set_at: string | null } | null;
  signatory: SignatorySetting | null;
  signature: SignatureSetting | null;
}
export type AgreementSettings = Record<EntityKey, EntityAgreementSettings>;

export interface AgreementSettingsResponse {
  settings: AgreementSettings;
  entities: { key: EntityKey; label: string; legal_name: string | null }[];
  documents: AgreementChoice[];
  documents_unavailable?: boolean;
  can_edit: boolean;
  /** Which companies use the client portal (Eric 2026-10-30: "CA and IL only" at launch). */
  portal?: Record<EntityKey, boolean>;
}

export interface AgreementPatch {
  entity_key: EntityKey;
  agreement_document_id?: string | null;
  signatory?: { name: string; title: string } | null;
  confirm_authority?: true;
}

const BASE = '/portal/settings/agreements';

export function getAgreementSettings(): Promise<AgreementSettingsResponse> {
  return adminFetch<AgreementSettingsResponse>(BASE);
}

export function saveAgreementSettings(patch: AgreementPatch): Promise<{ settings: AgreementSettings; countersign_started: boolean }> {
  return adminFetch(BASE, { method: 'PUT', body: JSON.stringify(patch) });
}

/** Turn the client portal on or off for one company (superadmin membership; audited). */
export function savePortalEnabled(entityKey: EntityKey, enabled: boolean): Promise<{ portal: Record<EntityKey, boolean> }> {
  return adminFetch(`${BASE}/portal`, { method: 'PUT', body: JSON.stringify({ entity_key: entityKey, enabled }) });
}

/**
 * One PNG for one or more companies with the same saved signatory; it is saved as their signature.
 * The server checks it (transparent PNG, sensible size).
 */
export function uploadSignatureImage(file: File, entityKeys: EntityKey[]): Promise<{ settings: AgreementSettings; countersign_started: EntityKey[] }> {
  const form = new FormData();
  form.append('entity_keys', entityKeys.join(','));
  form.append('image', file);
  return adminFetch(`${BASE}/signature-image`, { method: 'PUT', body: form });
}

export function removeSignatureImage(entityKey: EntityKey): Promise<{ settings: AgreementSettings }> {
  return adminFetch(`${BASE}/signature-image/${entityKey}`, { method: 'DELETE' });
}

/** The saved image, to preview (a Blob; the caller makes and revokes the object URL). */
export async function fetchSignatureImage(entityKey: EntityKey): Promise<Blob> {
  const headers = { ...(await getAuthHeaders() as Record<string, string>) };
  delete headers['Content-Type'];
  const res = await fetch(`${API_BASE_URL}/v1/admin${BASE}/signature-image/${entityKey}`, { headers });
  if (!res.ok) throw new Error(res.status === 404 ? 'No signature image saved.' : 'The signature image couldn’t be loaded.');
  return res.blob();
}
